import type { Actor } from '../../battle/types';
import type { ArenaInteractionsHandler } from '../interactions';

// Object id used in arena_attach / arena_release events for cannonballs stuck
// in the pit walls. Shared with the renderer-side bundle (playArenaEvent).
export const CANNONBALL_OBJECT_ID = 'cannonball';

type EmbeddedBall = {
  sourceSkillId: string;
  // Damage to deal when the ball is dislodged. Defaults to the amount the
  // original block absorbed - blocking delays the hit but does not erase it.
  pendingDamage: number;
};

type ThePitState = {
  // One ball per side at most. A second block on the same side breaks the
  // wall instead (see brokenWalls).
  embeddedBalls: Record<Actor, EmbeddedBall | null>;
  // Walls blown open by a second embedded ball. A broken wall can't hold a
  // ball anymore - later blocked cannons on that side lodge nothing.
  brokenWalls: Record<Actor, boolean>;
};

export const THE_PIT_INTERACTIONS: ArenaInteractionsHandler<ThePitState> = {
  id: 'the_pit',
  initialState: () => ({
    embeddedBalls: { p1: null, p2: null },
    brokenWalls: { p1: false, p2: false },
  }),

  onAttackResolved(ctx, state) {
    // Cannon shot blocked -> ball lodges in the defender's wall above them.
    if (ctx.attackSkill?.id === 'cannon' && ctx.reaction === 'block') {
      if (state.brokenWalls[ctx.defender]) {
        // wall already blown open - nothing left for the ball to lodge into
      } else if (state.embeddedBalls[ctx.defender]) {
        // Second ball into the same wall - the wall gives in. Pure spectacle:
        // both balls (and their pending damage) are gone with the wall.
        state.embeddedBalls[ctx.defender] = null;
        state.brokenWalls[ctx.defender] = true;
        ctx.emit({
          type: 'arena_break',
          side: ctx.defender,
          objectId: CANNONBALL_OBJECT_ID,
        });
      } else {
        state.embeddedBalls[ctx.defender] = {
          sourceSkillId: ctx.attackSkill.id,
          pendingDamage: Math.floor(ctx.reducedDamage),
        };
        ctx.emit({
          type: 'arena_attach',
          side: ctx.defender,
          objectId: CANNONBALL_OBJECT_ID,
          sourceSkillId: ctx.attackSkill.id,
        });
      }
    }

    // Earthshake-tagged attack -> every embedded ball falls and explodes.
    // Iteration order p1 -> p2 is fixed so event sequences stay deterministic.
    // All explosions are SIMULTANEOUS - collect the hits and deal them in one
    // batch so the renderer plays both reactions at once instead of serially.
    if (ctx.attackSkill?.effects?.includes('earthshake')) {
      const hits: { target: Actor; amount: number; launches: boolean; fromBehind: boolean }[] = [];
      for (const side of ['p1', 'p2'] as const) {
        const ball = state.embeddedBalls[side];
        if (!ball) continue;
        state.embeddedBalls[side] = null;
        ctx.emit({ type: 'arena_release', side, objectId: CANNONBALL_OBJECT_ID });
        // Skip damage if the target is already dead (e.g. earthshake itself
        // killed them) so we don't pile damage onto a 0-HP combatant. HP is
        // read pre-explosion for every side (the blasts are simultaneous).
        if (ball.pendingDamage > 0 && ctx.getHP(side) > 0) {
          // fromBehind: the ball hangs in the wall BEHIND the defender, so the
          // blast knocks them toward arena center - renderer mirrors pain-air.
          // A ball blasting off the wall always knocks the victim up - the
          // explosion goes off at their back regardless of what shot it in.
          hits.push({
            target: side,
            amount: ball.pendingDamage,
            launches: true,
            fromBehind: true,
          });
        }
      }
      if (hits.length > 0) ctx.dealDamageBatch(hits);
    }
  },
};
